import { Button, Input, Modal, Upload, message } from "antd";
import { useEffect, useState, lazy, Suspense } from "react";
import { CloudUploadOutlined, FileTextOutlined } from "@ant-design/icons";
import "./upload_signed_mandate.css";
import AutoCollectAPI from "../../services/api/autoCollect";
import { fileToBase64 } from "../../utility";
import { OTPInput } from "../../reactAdapter";

const SIGNED_MANDATE_MAX_SIZE_MB = 2;
const SIGNED_MANDATE_ALLOWED_TYPES = ["image/jpeg", "image/jpg"];
const SIGNED_MANDATE_ALLOWED_EXTENSIONS = ["jpg", "jpeg"];
const OTP_LENGTH = 6;
const OTP_RESEND_SECONDS = 30;

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || fallback;

const UploadSignedMandate = (props) => {
  const { url, transactionId, phone } = props;
  const [selectedFile, setSelectedFile] = useState(null);
  const [isModalOpen, setModalOpen] = useState(false);
  const [otp, setOtp] = useState("");
  const [resendTimer, setResendTimer] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);

  useEffect(() => {
    if (!isModalOpen || resendTimer <= 0) return;
    const timeout = setTimeout(() => setResendTimer((t) => t - 1), 1000);
    return () => clearTimeout(timeout);
  }, [isModalOpen, resendTimer]);

  const handleSendOtp = () => {
    setIsSendingOtp(true);
    AutoCollectAPI.generate_otp(url, transactionId)
      .then((response) => {
        if (response.status) {
          message.success("OTP sent successfully");
          setResendTimer(OTP_RESEND_SECONDS);
        } else {
          message.error(response?.message || "Unable to send OTP");
          closeModal();
        }
      })
      .catch((error) => {
        message.error(getErrorMessage(error, "Unable to send OTP"));
        closeModal();
      })
      .finally(() => {
        setIsSendingOtp(false);
      });
  };

  const handleResendOtp = () => {
    setIsSendingOtp(true);
    AutoCollectAPI.resend_otp(url, transactionId)
      .then((response) => {
        if (response.status) {
          message.success("OTP sent successfully");
          setOtp("");
          setResendTimer(OTP_RESEND_SECONDS);
        } else {
          message.error(response?.message || "Unable to send OTP");
        }
      })
      .catch((error) => {
        message.error(getErrorMessage(error, "Unable to send OTP"));
      })
      .finally(() => {
        setIsSendingOtp(false);
      });
  };

  const handleVerifyAndUpload = async () => {
    setIsUploading(true);
    try {
      const mandate = await fileToBase64(selectedFile);
      const response = await AutoCollectAPI.upload_mandate(url, transactionId, {
        mandate,
        otp,
      });
      if (response.status) {
        message.success("Signed mandate uploaded successfully");
        closeModal();
      } else {
        message.error(response?.message || "Unable to upload signed mandate");
        setOtp("");
      }
    } catch (error) {
      message.error(getErrorMessage(error, "Unable to upload signed mandate"));
      setOtp("");
    } finally {
      setIsUploading(false);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedFile(null);
    setOtp("");
    setResendTimer(0);
  };

  const handleBeforeUpload = (file) => {
    const extension = file.name.split(".").pop().toLowerCase();
    if (
      !SIGNED_MANDATE_ALLOWED_TYPES.includes(file.type) ||
      !SIGNED_MANDATE_ALLOWED_EXTENSIONS.includes(extension)
    ) {
      message.error("Only JPG images are allowed");
      return Upload.LIST_IGNORE;
    }
    if (file.size / 1024 / 1024 > SIGNED_MANDATE_MAX_SIZE_MB) {
      message.error(
        `File size must not exceed ${SIGNED_MANDATE_MAX_SIZE_MB}MB`,
      );
      return Upload.LIST_IGNORE;
    }
    setSelectedFile(file);
    setOtp("");
    if (!isModalOpen) {
      setModalOpen(true);
      handleSendOtp();
    }
    return Upload.LIST_IGNORE;
  };

  const uploadProps = {
    accept: ".jpg,.jpeg",
    showUploadList: false,
    multiple: false,
    beforeUpload: handleBeforeUpload,
  };

  return (
    <>
      <Upload {...uploadProps}>
        <Button>
          <CloudUploadOutlined /> Upload Signed Mandate
        </Button>
      </Upload>
      <Modal
        open={isModalOpen}
        onCancel={closeModal}
        footer={null}
        title="Verify Document Upload"
        width={480}
        destroyOnClose
      >
        <div className="signed-mandate-file-card">
          <FileTextOutlined className="signed-mandate-file-icon" />
          <div className="signed-mandate-file-info">
            <p className="signed-mandate-file-name">{selectedFile?.name}</p>
            <p className="signed-mandate-file-status">Ready to upload</p>
          </div>
          <Upload {...uploadProps}>
            <Button
              type="link"
              size="small"
              className="signed-mandate-link-button"
            >
              Change File
            </Button>
          </Upload>
        </div>
        <div className="signed-mandate-otp-section">
          <p>To securely attach this document, enter the OTP sent to</p>
          <p className="signed-mandate-otp-phone">
            <b>{phone}</b>
          </p>
          <OTPInput
            value={otp}
            autoFocus
            inputMode="numeric"
            onChange={(value) => {
              setOtp(value);
            }}
            length={6}
          />
          <p className="signed-mandate-resend">
            {resendTimer > 0 ? (
              <>
                Resend OTP in <b>{resendTimer}s</b>
              </>
            ) : (
              <Button
                type="link"
                size="small"
                onClick={handleResendOtp}
                loading={isSendingOtp}
                className="signed-mandate-link-button"
              >
                Resend OTP
              </Button>
            )}
          </p>
        </div>
        <Button
          type="primary"
          block
          size="large"
          loading={isUploading}
          disabled={otp.length !== OTP_LENGTH || isSendingOtp}
          onClick={handleVerifyAndUpload}
        >
          Verify & Upload Document
        </Button>
      </Modal>
    </>
  );
};

export default UploadSignedMandate;
